/**
 * May this attempt be recorded, how many times, and what do the +/− buttons do.
 *
 * Two layers that are easy to confuse. The GATES — the competition-wide and
 * per-session locks, the attempt cap, and `getSelfScoreState`, which answers
 * whether a competitor may score themselves at all — decide if an entry is
 * allowed. The COUNTERS — `applyOutcomeToggle`, `stepAttempts`,
 * `normalizeOutcomeCounts` — decide what the numbers become once it is.
 *
 * The counters are pure and heavily tested because a competitor drives them
 * with their thumb, one tap at a time, on a screen that has to stay consistent
 * whichever order the taps arrive in.
 */

/**
 * Resolve the per-competitor attempt cap. `maxAttempts` lives on the group
 * doc (`group.maxAttempts`) — that's the source of truth. The legacy
 * `settings.maxAttempts` (comp-wide cap from the old editor) is kept as a
 * fallback so existing competitions don't lose their limit when the admin
 * hasn't migrated to per-group values yet.
 *
 * Returns a positive integer cap, or null for "unlimited".
 *
 *   group     — group doc (or null for legacy lookup)
 *   settings  — competition.settings (for the legacy fallback)
 */
export function getGroupMaxAttempts(group, settings) {
  const g = group?.maxAttempts;
  if (g != null && g > 0) return g;
  const s = settings?.maxAttempts;
  if (s != null && s > 0) return s;
  return null;
}

/**
 * The finals session (type "finals") linked to `groupId` whose
 * `finals.routeIds` contains `routeId`, or null. Used to decide whether a
 * route is being climbed in a finals context.
 */
export function findFinalsSessionForRoute(sessions, groupId, routeId) {
  if (!groupId || !routeId) return null;
  return (sessions || []).find(s =>
    s && s.type === "finals" && s.finals &&
    (s.groupIds || []).includes(groupId) &&
    (s.finals.routeIds || []).includes(routeId)
  ) || null;
}

/**
 * Effective attempt cap for a route a competitor of `group` is climbing.
 * If the route belongs to a finals session for that group and that finals
 * has its own `finals.maxAttempts`, that wins; otherwise falls back to the
 * group's qualification cap (`getGroupMaxAttempts`). Single source of truth
 * for every attempt-entry surface (judge modal, self-attempt modal) and the
 * scorecard so finals attempt limits never disagree across the app.
 */
export function resolveMaxAttempts(sessions, group, settings, routeId) {
  const fs = findFinalsSessionForRoute(sessions, group?.id, routeId);
  const f = fs?.finals?.maxAttempts;
  if (f != null && f > 0) return f;
  return getGroupMaxAttempts(group, settings);
}

/**
 * True when recording new attempts is locked for this competition/session.
 * A competition-wide lock (`competition.attemptsLocked`) freezes everything;
 * a per-session lock (`session.attemptsLocked`) freezes just that session.
 * Either blocks the judge modal, JAM modal, and the map entry surfaces.
 * Pure OR — gymAdmins/organizers can still apply explicit admin corrections
 * (that bypass is handled by the caller via actorRole, not here).
 */
export function isAttemptRecordingLocked(competition, session) {
  return !!(competition?.attemptsLocked || session?.attemptsLocked);
}

/**
 * May this competitor score themselves on a route right now — and if not, why?
 *
 * ONE answer for a question three surfaces ask (Competition tab route list,
 * map, Routes tab) and that the server asks again in `canSelfScoreAttempt`
 * (firestore.rules). They used to answer it separately and disagree: the map
 * required a RUNNING session, while the route list also allowed `paused` and
 * the post-session window. The same route was therefore scoreable in one place
 * and inert in the other — and on the map the tap fell through to the ordinary
 * RouteModal, where "climbed" means a gym ascent, not a competition result.
 *
 * `reason` is not decoration: it is what the read-only modal tells the climber
 * instead of a dead tap.
 *
 * @param {Object} competition
 * @param {Object} registration - the caller's OWN registration doc
 * @param {Object} group - the group named by that registration
 * @param {Object} session - the session named by that registration
 * @returns {{canScore: boolean, reason: null|"notRegistered"|"closed"|"judged"|"locked"|"notStarted"}}
 */
export function getSelfScoreState(competition, registration, group, session) {
  if (!registration || registration.role !== "competitor") {
    return { canScore: false, reason: "notRegistered" };
  }
  // `closing` is the brief freeze while closeCompetition snapshots the
  // rankings; the server rejects writes from that moment, so the UI must not
  // still offer them.
  if (competition?.status === "closed" || competition?.status === "closing") {
    return { canScore: false, reason: "closed" };
  }
  if (group?.mode !== "jam") return { canScore: false, reason: "judged" };
  if (isAttemptRecordingLocked(competition, session)) {
    return { canScore: false, reason: "locked" };
  }
  const status = session?.status;
  // A finished session does not close the scorecard — the climber fills in what
  // they could not record mid-climb. Closing it is the organizer's act: the
  // per-session or competition-wide lock above, or closing the competition.
  // There used to be a per-competitor "hand in" that closed it too; it closed
  // nothing anyone relied on (an unsubmitted card still scored, still ranked,
  // and the session ended on the organizer's clock either way), so the lock is
  // now the only switch.
  if (status === "running" || status === "paused" || status === "finished") {
    return { canScore: true, reason: null };
  }
  return { canScore: false, reason: "notStarted" };
}

/**
 * Who owns a tap on the selected route — the route library, or the running
 * competition?
 *
 * ONE answer for a question `App.js` asks twice in the SAME render: once to
 * decide whether `<RouteModal>` may mount, once in the effect that swaps the
 * selection over to the competition modal. That simultaneity is the fix for a
 * real bug — the swap used to be decided only in the effect, which runs AFTER
 * the render that already mounted the ordinary route modal, so every
 * competition tap flashed it on screen first.
 *
 * @param {Object|null} route - the selected route (`selRoute`)
 * @param {boolean} editMode - the selection came from an explicit "edit this
 *   route" (map long-press, Routes tab pencil). Never claimed by the
 *   competition, or an entered setter / gym admin would lose the editor on
 *   their own group's routes for the whole competition.
 * @param {Object|null} compMapContext - the caller's relation to the running
 *   competition (`useMainApp`); null for anyone not entered
 * @param {Set|null} myScorecardRouteIds - the routes the caller actually
 *   competes on. NOT every competition route: the self-save rule checks the
 *   climber, the session and the group mode — not the route — so a scorecard
 *   opened on another group's boulder would happily save.
 * @param {Array|null} myAttempts - the caller's own attempt rows, or `null`
 *   while the first snapshot is still in flight
 * @returns {null|"route"|"attempt"|"judge"|"wait"} `null` = nothing selected.
 *   `"wait"` = the competition owns the tap but the scorecard cannot be opened
 *   yet — show NO modal, not the wrong one.
 */
export function resolveRouteTapTarget(route, editMode, compMapContext, myScorecardRouteIds, myAttempts) {
  if (!route) return null;
  // The ordinary RouteModal must never open on a competition route for someone
  // entered in it: "climbed" there means a gym ascent, which is not what a
  // competition result is, and the two are indistinguishable afterwards.
  // Setters and gym admins who are NOT entered have no compMapContext and keep
  // the RouteModal, which is how they still edit the route.
  if (editMode || !compMapContext || !myScorecardRouteIds?.has(route.id)) return "route";
  // A judge records through the event-based editor, which reads no attempt row
  // of their own — so it never waits.
  if (compMapContext.role === "judge") return "judge";
  // Every competitor lands in the attempt modal, including a judged group's —
  // read-only there, because the score is the judge's to record. But a
  // scorecard opened on a row that has not loaded yet would show 0/0 and
  // overwrite a real score on save, so an unloaded list waits instead. The tap
  // is not lost: the caller re-asks when the rows arrive.
  return myAttempts ? "attempt" : "wait";
}

/**
 * Apply a Zone / Top "reached" toggle to an attempt's four fields.
 *
 * ONE transition for a rule three recording surfaces used to answer three
 * different ways: the JAM scorecard, the admin editor's per-route rows, and
 * the admin editor's single-route card. R-SC-01 says a top IS a zone, so
 * `topped && !zoneReached` is not a state the domain has — yet the card could
 * save exactly that, and the row cleared the top instead of refusing.
 *
 * **Ticking a switch ON spends an attempt.** The count on the card is the
 * tries that came BEFORE the one being recorded, so "2 attempts" plus a Top
 * tick is a top on the 3rd — the switch IS the attempt. The flash case was
 * always this rule (0 → 1); it now holds at every count, which is also how the
 * judge modal has always recorded: there each Fall / Zone / Top tap is an
 * attempt of its own, and the two surfaces cannot mean different things by the
 * same tap. `maxAttempts` caps the way up, so a capped competition can never
 * be pushed past its own limit by a tick.
 *
 * The two directions are deliberately NOT symmetric:
 *
 * - Ticking Top implies the zone, so it switches the zone on for you.
 * - Unticking Zone under a top is REFUSED, not cascaded. Cascading would wipe
 *   attempt counts the climber entered, on one tap, with no undo inside the
 *   modal. Untick Top first — that is the correction they actually meant, and
 *   it keeps "tried 3×, not topped" intact.
 * - Unticking does NOT give the attempt back. The tries already happened; a
 *   top that turned out not to be one leaves "tried N×, not done yet", which
 *   is exactly the state the − stepper is there to walk back down.
 *
 * Callers disable the zone switch while `topped` so the refusal is visible
 * rather than a dead tap; this function is the defence behind that.
 *
 * @param {{zoneAttempts:number, zoneReached:boolean, topAttempts:number, topped:boolean}} values
 * @param {"zone"|"top"} kind - which switch was flipped
 * @param {boolean} next - the position it was flipped to
 * @param {boolean} [hasZones=true] - `competition.settings.hasZones`. A
 *   zones-off competition has no zone fields to keep consistent, so ticking Top
 *   leaves them untouched. (The judge Cloud Function still mirrors them there —
 *   it derives the aggregate from the event stream without reading the
 *   competition settings — but nothing reads the result: `calculateRanking`
 *   keeps zone data out of a zones-off ranking entirely.)
 * @param {{maxAttempts?: number|null}} [opts] - `maxAttempts` is the per-group
 *   attempt cap (`resolveMaxAttempts`), the same one `stepAttempts` enforces
 *   for the +/− buttons. Left out, the tick is uncapped.
 * @returns {Object} the new values (extra keys on `values` are preserved), or
 *   `values` itself when the toggle is refused
 */
export function applyOutcomeToggle(values, kind, next, hasZones = true, { maxAttempts = null } = {}) {
  const v = {
    zoneAttempts: 0, zoneReached: false, topAttempts: 0, topped: false,
    ...(values || {}),
  };
  // Which attempt did the outcome fall on? The one after the tries already on
  // the card — the switch is the attempt being recorded. A fresh 0 becomes 1
  // (the flash), a 2 becomes 3. Never past the cap: at `maxAttempts` the
  // climber has no further attempt to spend, so the tick records the outcome
  // on the last one they had.
  const achievedOn = (n) => (maxAttempts ? Math.min(n + 1, maxAttempts) : n + 1);

  if (kind === "zone") {
    // A top always counts as a zone — the zone cannot go off underneath it.
    if (!next && v.topped) return values;
    const out = { ...v, zoneReached: next };
    if (next) {
      out.zoneAttempts = achievedOn(out.zoneAttempts);
      // The zone fell on attempt N, so at least N attempts have happened —
      // the same `topAttempts >= zoneAttempts` invariant `stepAttempts`
      // enforces when the zone stepper walks past the top.
      if (out.zoneAttempts > out.topAttempts) out.topAttempts = out.zoneAttempts;
    }
    return out;
  }
  if (kind !== "top") return values;
  const out = { ...v, topped: next };
  if (next) {
    out.topAttempts = achievedOn(out.topAttempts);
    if (hasZones) {
      // A top IS a zone (R-SC-01). If the zone had NOT been reached before
      // this tick, it fell on the top attempt itself — the earlier tries are
      // precisely the ones that did not reach it. A zone already claimed
      // keeps its own attempt, unless it claims a later one than the top,
      // which `zoneAttempts > topAttempts` would.
      if (!v.zoneReached || out.zoneAttempts > out.topAttempts) out.zoneAttempts = out.topAttempts;
      out.zoneReached = true;
    }
  }
  // Unticking Top keeps the counts: "tried 3x, not done yet" is a real state.
  return out;
}

/**
 * The lowest attempt count a Zone/Top stepper may be stepped down to.
 *
 * Reaching something takes at least one attempt, so a ticked outcome floors at
 * 1 — "reached on attempt 0" is not a slow day, it is a contradiction. Untick
 * the outcome first and the floor drops back to 0, which is the honest state
 * for a route nobody has been on.
 *
 * `applyOutcomeToggle` already bumps 0 → 1 when the switch goes ON; this is the
 * other half of the same rule, for the − button that could walk it back down.
 */
export function minAttemptsForOutcome(reached) {
  return reached ? 1 : 0;
}

/**
 * One tap on a Zone/Top attempt stepper, as a pure transition.
 *
 * Three surfaces open-coded these four handlers — the JAM card, the admin
 * single-route card, the admin per-route row — each carrying a comment saying
 * it mirrors the other two. The mirror is what broke: the `−` under Top was
 * dead on all three whenever neither outcome was ticked, which is exactly the
 * state an organizer is in when they typed too many attempts and want to walk
 * the number back.
 *
 * Two rules decide every step:
 *
 * 1. **While neither outcome is reached, the two counts are ONE number.** The
 *    climber has simply been on the boulder N times; there is no zone attempt
 *    distinct from a top attempt yet. Both directions move the pair together.
 * 2. **`topAttempts >= zoneAttempts`** (`hasZones` only) — the total cannot be
 *    fewer than the attempt the zone fell on. This is the guard that misfired:
 *    it compared the step against a zone count it was about to move down as
 *    well, so in the linked state it measured against a value one higher than
 *    the one that would be stored, and refused every time. Zones-off is the
 *    other half — there is no zone to stay above, so the guard does not apply.
 *
 * `maxAttempts` caps the way up, `minAttemptsForOutcome` floors the way down.
 *
 * Returns the next values (extra keys preserved), or `null` when the step is
 * refused — which is also what the `−`/`+` buttons derive `disabled` from, so
 * a button that cannot do anything reads as disabled instead of merely dead.
 */
export function stepAttempts(values, kind, delta, { maxAttempts = null, hasZones = true } = {}) {
  if (kind !== "zone" && kind !== "top") return null;
  if (delta !== 1 && delta !== -1) return null;
  const v = {
    zoneAttempts: 0, zoneReached: false, topAttempts: 0, topped: false,
    ...(values || {}),
  };
  const linked = !v.zoneReached && !v.topped;
  const field = kind === "zone" ? "zoneAttempts" : "topAttempts";
  const other = kind === "zone" ? "topAttempts" : "zoneAttempts";
  const reached = kind === "zone" ? v.zoneReached : v.topped;
  const next = v[field] + delta;

  if (delta > 0) {
    if (maxAttempts && v[field] >= maxAttempts) return null;
  } else {
    if (v[field] <= minAttemptsForOutcome(reached)) return null;
    if (kind === "top" && hasZones && !linked && next < v.zoneAttempts) return null;
  }

  const out = { ...v, [field]: next };
  if (linked) out[other] = next;
  // Stepping the zone past the top drags the top up with it: the zone was
  // reached on attempt N, so there have been at least N attempts.
  else if (kind === "zone" && next > v.topAttempts) out.topAttempts = next;
  return out;
}

/**
 * Bring an attempt's four fields into a state the domain actually has, before
 * they are written.
 *
 * Two rules, both of which the recording surfaces had been re-deriving inline:
 *
 * 1. **A reached outcome took at least one attempt** (`minAttemptsForOutcome`).
 *    The steppers can no longer produce a 0 under a tick, but a row written
 *    before that rule existed still can, and re-saving it unchanged would
 *    persist the contradiction for another round.
 * 2. **A top IS a zone** (R-SC-01, `hasZones` only) — `zoneReached` follows
 *    `topped`, and the zone was reached on the top attempt or an earlier one,
 *    never a later one, which is what `zoneAttempts > topAttempts` would claim.
 *
 * A zones-off competition has no zone fields to keep consistent, so they are
 * left exactly as they are — same as `applyOutcomeToggle`.
 *
 * Pure: returns a new object with any extra keys preserved.
 */
export function normalizeOutcomeCounts(values, hasZones = true) {
  const out = {
    zoneAttempts: 0, zoneReached: false, topAttempts: 0, topped: false,
    ...(values || {}),
  };
  if (out.topped) {
    if (out.topAttempts < 1) out.topAttempts = 1;
    if (hasZones) {
      // Zone never claimed, claimed at 0, or claimed LATER than the top? Then
      // the zone was reached on the top attempt itself. "Tried 3x for the zone
      // without getting it, then topped on the 5th" means the zone fell on 5,
      // not on 3.
      if (!out.zoneReached || out.zoneAttempts < 1 || out.zoneAttempts > out.topAttempts) {
        out.zoneAttempts = out.topAttempts;
      }
      out.zoneReached = true;
    }
  }
  if (hasZones && out.zoneReached && out.zoneAttempts < 1) out.zoneAttempts = 1;
  return out;
}
